/**
 * laylo-editorial-menu.js
 *
 * Three responsibilities:
 *   1. Reveal animation — IntersectionObserver toggles .is-visible on
 *      .lem-reveal elements when they enter the viewport.
 *      CSS handles the transition entirely; JS only adds the class.
 *
 *   2. Sidebar active state — IntersectionObserver watches each content
 *      region (.lem__hero, .lem__intro, .lem__menu, .lem__reserve).
 *      When a region is in view the corresponding sidebar link receives
 *      .is-active. rootMargin accounts for the fixed bottom bar height
 *      on mobile. A ResizeObserver on the sidebar rebuilds the observer
 *      whenever the bar height changes (orientation change, zoom, etc.)
 *      so the rootMargin is always accurate.
 *
 *   3. Smooth anchor scrolling — delegated click listener on the sidebar
 *      nav. scrollIntoView({ behavior: 'smooth' }) on the target section.
 *      Respects prefers-reduced-motion.
 *
 * Performance:
 *   - All IntersectionObserver + ResizeObserver — no scroll events
 *   - Single delegated click listener on the sidebar nav
 *   - ResizeObserver callback is debounced (150ms) to absorb the burst
 *     of resize events that fires during orientation change
 *   - No library dependencies
 *   - Batch reads before writes in all callbacks
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /**
   * Simple debounce — collapses rapid calls into one after `wait` ms.
   * @param {Function} fn
   * @param {number}   wait — ms
   * @returns {Function}
   */
  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Build an IntersectionObserver that marks sidebar links active
   * when their corresponding region enters the viewport.
   * rootMargin bottom is offset by the bar height so the observer
   * treats the viewport as ending above the fixed bottom bar.
   *
   * IntersectionObserver options are immutable after construction —
   * call this function to get a fresh observer whenever barHeight changes,
   * and disconnect the previous one first.
   *
   * @param {NodeList} sidebarLinks
   * @param {NodeList} regions
   * @param {number}   barHeight — px height of the fixed bottom bar (0 on desktop)
   * @returns {IntersectionObserver}
   */
  function buildActiveObserver(sidebarLinks, regions, barHeight) {
    const bottomMargin = barHeight > 0 ? `-${barHeight}px` : '-10%';

    const observer = new IntersectionObserver(
      (entries) => {
        // Batch reads — collect intersecting entries sorted by ratio
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (!visible.length) return;

        const activeId = visible[0].target.id;

        // Batch writes — update all links in one pass
        sidebarLinks.forEach(link => {
          const isActive = link.dataset.target === activeId;
          link.classList.toggle('is-active', isActive);
          link.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      },
      {
        threshold: [0.1, 0.5],
        rootMargin: `-10% 0px ${bottomMargin} 0px`,
      }
    );

    regions.forEach(region => observer.observe(region));
    return observer;
  }

  /**
   * Initialise one instance of the editorial menu section.
   * Called once per .lem element found on the page.
   * @param {HTMLElement} root — the .lem section wrapper
   */
  function initEditorialMenu(root) {
    const sidebarNav   = root.querySelector('.lem__sidebar-nav');
    const sidebarLinks = root.querySelectorAll('.lem__sidebar-link');
    const sidebar      = root.querySelector('.lem__sidebar');
    const regions      = root.querySelectorAll('[data-observe]');
    const revealEls    = root.querySelectorAll('.lem-reveal');

    // Set sidebar top offset to sit below the site's header/navigation.
    // Looks for common header selectors and falls back to 0.
    try {
      const headerSelectors = [
        '#shopify-section-header',
        'header.site-header',
        '.site-header',
        'header.header',
        'header',
        '.header',
        '#Header'
      ];
      let headerEl = null;
      for (const sel of headerSelectors) {
        const found = document.querySelector(sel);
        if (found) { headerEl = found; break; }
      }
      const headerHeight = headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 0;
      // Apply per-section so multiple .lem on a page behave consistently
      root.style.setProperty('--lem-sidebar-top', headerHeight + 'px');
    } catch (e) {
      // ignore measurement failures — default CSS fallback will apply
    }

    // ── 1. Reveal animation ───────────────────────────────────────────────
    if (!prefersReducedMotion && revealEls.length) {
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12 }
      );
      revealEls.forEach(el => revealObserver.observe(el));
    } else {
      // Reduced motion — show all immediately, no observer needed
      revealEls.forEach(el => el.classList.add('is-visible'));
    }

    // ── 2. Sidebar active state with orientation-aware rootMargin ─────────
    if (sidebarLinks.length && regions.length) {
      /*
        Read the sidebar's rendered height to set the initial rootMargin.
        On desktop the sidebar is not fixed — offsetHeight is its full
        column height, but isMobile will be false so barHeight stays 0.
      */
      const mq = window.matchMedia('(max-width: 959px)');
      const getBarHeight = () =>
        mq.matches && sidebar ? sidebar.offsetHeight : 0;

      // Create the initial observer
      let activeObserver = buildActiveObserver(
        sidebarLinks, regions, getBarHeight()
      );

      /*
        ResizeObserver on the sidebar element.
        Fires when the sidebar's dimensions change — this includes:
          • Orientation change (portrait ↔ landscape)
          • Browser zoom level change
          • Any future CSS change to the bar height
        Debounced at 150ms to absorb the burst of callbacks that fires
        during orientation change while the browser recalculates layout.
        Each rebuild: disconnect old observer → create new one with
        updated rootMargin → observe all regions again.
      */
      if ('ResizeObserver' in window && sidebar) {
        const rebuild = debounce(() => {
          activeObserver.disconnect();
          activeObserver = buildActiveObserver(
            sidebarLinks, regions, getBarHeight()
          );
        }, 150);

        const sizeObserver = new ResizeObserver(rebuild);
        sizeObserver.observe(sidebar);
      }
    }

    // ── 3. Smooth anchor scrolling ────────────────────────────────────────
    if (sidebarNav) {
      sidebarNav.addEventListener('click', (e) => {
        const link = e.target.closest('.lem__sidebar-link');
        if (!link) return;

        const targetId = link.dataset.target;
        const targetEl = document.getElementById(targetId);
        if (!targetEl) return;

        e.preventDefault();

        targetEl.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });

        // Move focus to the region for keyboard users
        if (!targetEl.hasAttribute('tabindex')) {
          targetEl.setAttribute('tabindex', '-1');
        }
        targetEl.focus({ preventScroll: true });
      });
    }
  }

  // Initialise all editorial menu sections on the page
  document.querySelectorAll('.lem').forEach(initEditorialMenu);

})();

